import { createContext, useContext } from "react";

import type {
  AdditionalService,
  FindFlightOffersFlightSegmentV3,
  FindFlightOffersFlightV3,
  FindFlightOffersOfferFlightsOverviewV3,
  FindFlightOffersOfferV3,
  GetFlightAvailabilityTokenBodyInputDto,
  PackageBannerV3,
} from "@/types/codegen";
import type { FlightSearchType } from "@/types/types";

import type { FlightOffer } from "./FlightSelectionPayloadContextProvider";

export type AdditionalServiceData = {
  service: AdditionalService;
  mandatory: boolean;
};

export type ServicesSelection = Pick<
  GetFlightAvailabilityTokenBodyInputDto,
  "mandatoryServices" | "optionalServices"
>;

export type FlightType = FindFlightOffersFlightV3 & {
  segments: FindFlightOffersFlightSegmentV3[];
};

export type OfferType = FindFlightOffersOfferV3 & {
  flight: FlightType;
};

export type MappedFlightOffer = Omit<
  FindFlightOffersOfferV3,
  "flightsOverview"
> & {
  flightsOverview: (FindFlightOffersOfferFlightsOverviewV3 & {
    departureFlight: FlightType;
    returnFlight?: FlightType;
  })[];
  searchResultToken: string;
};

export type FullOfferType = MappedFlightOffer | PackageBannerV3;

export type FlightPayload = (FlightOffer & ServicesSelection) | null;

export interface FlightSelectionPayloadContextValue {
  selectedOfferIndex: string | null;
  selectedDepartureFlightId: string | null;
  selectedReturnFlightId: string | null;
  selectedFlightPayload: FlightPayload;
  selectedFlightSearchSource: FlightSearchType | null;
  handleDepartureFlightSelection: (
    offer: MappedFlightOffer,
    flightId: string,
    queryType: FlightSearchType | null,
  ) => void;
  handleReturnFlightSelection: (
    offer: MappedFlightOffer,
    flightId: string,
  ) => void;
  clearFlightSelection: () => void;
  getTravellerServices: (travellerId: string) => AdditionalServiceData[];
  setTravellerServices: (
    travellerId: string,
    service: AdditionalServiceData[],
  ) => void;
  removeTravellerServices: (travellerId: string) => void;
  promoCode: string | null;
  setPromoCode: (code: string | null) => void;
}

export const FlightSelectionPayloadContext =
  createContext<FlightSelectionPayloadContextValue | null>(null);

export const useFlightSelectionPayloadContext = () => {
  const context = useContext(FlightSelectionPayloadContext);
  if (!context) {
    throw new Error(
      "useFlightSelectionPayloadContext must be used within a FlightSelectionPayloadContextProvider",
    );
  }
  return context;
};
